import { Badge } from "@/components/ui/badge";
import { formatDistanceToNow } from "date-fns";
import { Email, EmailGroup, UserLabel } from "@/types/email";
import { EmailCategory, getAnalysisLabels } from "@/lib/llm/types";
import {
  IconCircle,
  IconChevronDown,
  IconChevronRight,
  IconPaperclip,
  IconAlertCircle,
  IconClock,
  IconShield,
  IconArrowDown,
  IconPointFilled,
  IconCircleCheck,
  IconInbox,
  IconTrash,
  IconArchive,
  IconCircleX,
  IconGripVertical,
  IconStar,
} from "@tabler/icons-react";
import { useState, useCallback } from "react";
import { cn } from "@/lib/utils";
import { getTextColorForBackground } from "@/lib/utils/label-colors";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LabelEditor } from "./label-editor";
import { AIAnalysisPopover } from "./ai-analysis-popover";

interface EmailCardProps {
  email: Email | EmailGroup;
  isGroup?: boolean;
  isExpanded?: boolean;
  onToggleExpand?: (e: React.MouseEvent) => void;
  onClick?: (email: Email | EmailGroup, e: React.MouseEvent) => void;
  onAction?: (action: "delete" | "archive", email: Email) => void;
  onMoveEmail?: (email: Email, category: EmailCategory) => void;
  onLabelChange?: (email: Email, oldLabel: string, newLabel: string) => void;
  onAnalysisUpdate?: (
    email: Email,
    analysis: import("@/lib/llm/types").EmailAnalysis
  ) => void;
  onStarToggle?: (email: Email, starred: boolean) => void;
  onMouseEnter?: () => void;
  className?: string;
}

interface PriorityConfig {
  className: string;
  tooltip: string;
  label: "Important" | "Normal" | "Low";
}

export function EmailCard({
  email,
  isGroup = false,
  isExpanded = false,
  onToggleExpand,
  onClick,
  onAction,
  onMoveEmail,
  onLabelChange,
  onAnalysisUpdate,
  onStarToggle,
  onMouseEnter,
  className,
}: EmailCardProps) {
  const isEmailGroup = (email: Email | EmailGroup): email is EmailGroup => {
    return "emails" in email;
  };

  const displayEmail = isEmailGroup(email) ? email.latestEmail : email;
  const isRead = isEmailGroup(email)
    ? email.emails.every((e) => e.isRead)
    : displayEmail.isRead;

  // Extract email address from sender string like "Name <email@domain.com>"
  const getSenderEmail = (sender: string): string => {
    const match = sender.match(/<([^>]+)>/) || sender.match(/([^\s]+@[^\s]+)/);
    return match ? match[1].trim() : sender;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    // If less than 24 hours old
    if (diffInHours < 24) {
      return {
        isRecent: true,
        formatted: formatDistanceToNow(date, { addSuffix: true }),
      };
    }

    return {
      isRecent: false,
      formatted: formatDistanceToNow(date, { addSuffix: true }),
    };
  };

  const getSenderName = (sender: string) => {
    const match = sender.match(/^"?([^"<]+)"?\s*(?:<[^>]+>)?/);
    return match ? match[1].trim() : sender;
  };

  const renderLabels = (targetEmail: Email) => {
    if (targetEmail.analysisState === "preview") {
      return (
        <>
          {targetEmail.gmailLabels?.map((label) => (
            <span
              key={`${targetEmail.id}-gmail-${label}`}
              className="rounded-full border border-slate-200 bg-slate-100 px-1.5 py-0.5 text-[9px] font-medium text-slate-600"
              title="Existing Gmail label"
            >
              {label}
            </span>
          ))}
        </>
      );
    }

    return (
      <>
        {getAnalysisLabels(targetEmail.aiAnalysis)
          .filter(
            (label) => !targetEmail.userLabels?.some((ul) => ul.name === label)
          )
          .map((label, idx) => (
            <LabelEditor
              key={`${targetEmail.id}-${label}-${idx}`}
              currentLabel={label}
              senderEmail={getSenderEmail(targetEmail.sender)}
              emailId={targetEmail.id}
              onLabelChange={(oldLabel, newLabel) =>
                onLabelChange?.(targetEmail, oldLabel, newLabel)
              }
            />
          ))}
        {targetEmail.userLabels?.map((userLabel) => (
          <LabelEditor
            key={userLabel.id}
            currentLabel={userLabel.name}
            senderEmail={getSenderEmail(targetEmail.sender)}
            emailId={targetEmail.id}
            onLabelChange={(oldLabel, newLabel) =>
              onLabelChange?.(targetEmail, oldLabel, newLabel)
            }
          />
        ))}
        {targetEmail.aiAnalysis && (
          <AIAnalysisPopover
            analysis={targetEmail.aiAnalysis}
            gmailMessageId={targetEmail.id}
            onAnalysisUpdate={(analysis) =>
              onAnalysisUpdate?.(targetEmail, analysis)
            }
          />
        )}
      </>
    );
  };

  // McMaster-style: labels are just plain text, no colored backgrounds

  return (
    <div
      className={cn(
        "relative group",
        "transition-colors duration-75",
        "hover:bg-muted/50",
        "cursor-pointer",
        "border-b border-border/40",
        // Unread left border indicator
        !isRead && "border-l-2 border-l-blue-500",
        className
      )}
      onClick={(e: React.MouseEvent) => onClick?.(email, e)}
      tabIndex={0}
      role="button"
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick?.(email, e as any);
        }
      }}
      onMouseEnter={onMouseEnter}
    >
      {/* Group Header - Always show for groups */}
      {isGroup && isEmailGroup(email) && (
        <div className="px-1.5 py-1">
          <div className="flex items-center justify-between gap-1">
            <div className="flex items-center gap-1 min-w-0 flex-1">
              <span
                className={cn(
                  "font-medium truncate text-[11px]",
                  isRead ? "text-muted-foreground" : "text-foreground"
                )}
              >
                {getSenderName(displayEmail.sender)}
              </span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleExpand?.(e);
                }}
                className={cn(
                  "flex items-center gap-0.5 text-[9px] px-1 py-0.5",
                  "text-muted-foreground hover:text-foreground"
                )}
              >
                {isEmailGroup(email) && <span>{email.count}</span>}
                <IconChevronDown
                  className={cn("h-2.5 w-2.5", isExpanded && "rotate-180")}
                />
              </button>
            </div>
            <div className="flex items-center gap-1.5 text-[9px] text-muted-foreground shrink-0">
              {email.emails.some(
                (e) => e.aiAnalysis?.priority === "important"
              ) && (
                <span
                  className="text-[8px] px-1 py-0.5 rounded bg-amber-500/15 text-amber-600 dark:text-amber-400 font-medium"
                  title="Contains important emails"
                >
                  !
                </span>
              )}
              {email.emails.some((e) => e.hasAttachments) && (
                <span title="Contains attachments">
                  <IconPaperclip className="h-2.5 w-2.5" />
                </span>
              )}
              {formatDate(displayEmail.receivedAt).formatted}
            </div>
          </div>
        </div>
      )}

      {/* Regular Email or Collapsed Group Content */}
      {(!isGroup || !isExpanded) && !isEmailGroup(email) && (
        <div className="px-2 py-1.5">
          {/* Row 1: Sender + indicators + date */}
          <div className="flex items-center justify-between gap-1">
            <div className="flex items-center gap-1.5 min-w-0 flex-1">
              {/* Star button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onStarToggle?.(displayEmail, !displayEmail.isStarred);
                }}
                className={cn(
                  "shrink-0 p-0.5 -ml-0.5 rounded transition-colors",
                  displayEmail.isStarred
                    ? "text-yellow-500 hover:text-yellow-600"
                    : "text-muted-foreground/40 hover:text-yellow-500"
                )}
                title={displayEmail.isStarred ? "Unstar" : "Star"}
              >
                <IconStar
                  className={cn(
                    "h-3 w-3",
                    displayEmail.isStarred && "fill-current"
                  )}
                />
              </button>
              {/* Unread dot */}
              {!isRead && (
                <div className="w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0" />
              )}
              <span
                className={cn(
                  "truncate text-[11px]",
                  isRead
                    ? "text-muted-foreground"
                    : "font-semibold text-foreground"
                )}
              >
                {getSenderName(displayEmail.sender)}
              </span>
              {displayEmail.aiAnalysis?.priority === "important" && (
                <span
                  className="text-[8px] px-1 py-0.5 rounded bg-amber-500/15 text-amber-600 dark:text-amber-400 font-medium shrink-0"
                  title="Important"
                >
                  !
                </span>
              )}
              {displayEmail.aiAnalysis?.priority === "low" && (
                <span
                  className="text-[8px] px-1 py-0.5 rounded bg-gray-500/15 text-gray-500 font-medium shrink-0"
                  title="Low Priority"
                >
                  ↓
                </span>
              )}
              {displayEmail.hasAttachments && (
                <span title="Has attachments">
                  <IconPaperclip className="h-2.5 w-2.5 text-muted-foreground shrink-0" />
                </span>
              )}
              {displayEmail.threadCount && displayEmail.threadCount > 1 && (
                <span
                  className="text-[8px] px-1 py-0.5 rounded bg-muted text-muted-foreground font-medium shrink-0"
                  title={`${displayEmail.threadCount} messages in thread`}
                >
                  {displayEmail.threadCount}
                </span>
              )}
            </div>
            <span className="text-[9px] text-muted-foreground shrink-0">
              {formatDate(displayEmail.receivedAt).formatted}
            </span>
          </div>

          {/* Row 2: Subject */}
          <h3
            className={cn(
              "text-[11px] truncate",
              !isRead && "font-medium text-foreground",
              isRead && "text-foreground/70"
            )}
          >
            {displayEmail.subject}
          </h3>

          {/* Row 3: Snippet + labels */}
          <div className="flex items-center justify-between gap-1">
            <p className="text-[10px] text-muted-foreground truncate flex-1">
              {displayEmail.snippet}
            </p>
            {/* Labels + info icon */}
            <div className="flex items-center gap-1 shrink-0">
              {renderLabels(displayEmail)}
            </div>
          </div>
        </div>
      )}

      {/* Expanded Group Emails */}
      {isGroup && isEmailGroup(email) && isExpanded && (
        <div className="ml-3">
          {email.emails.map((groupEmail) => {
            const currentCategory =
              groupEmail.aiAnalysis?.category || "do_nothing";
            return (
              <div
                key={groupEmail.id}
                className={cn(
                  "group/email hover:bg-muted/30 transition-colors",
                  !groupEmail.isRead && "bg-blue-500/5"
                )}
              >
                <div className="px-1.5 py-1">
                  {/* Email Content */}
                  <div className="flex items-center justify-between gap-1">
                    <div
                      className="flex items-center gap-1.5 flex-1 cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        onClick?.(groupEmail, e);
                      }}
                    >
                      {/* Star button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onStarToggle?.(groupEmail, !groupEmail.isStarred);
                        }}
                        className={cn(
                          "shrink-0 p-0.5 rounded transition-colors",
                          groupEmail.isStarred
                            ? "text-yellow-500 hover:text-yellow-600"
                            : "text-muted-foreground/40 hover:text-yellow-500"
                        )}
                        title={groupEmail.isStarred ? "Unstar" : "Star"}
                      >
                        <IconStar
                          className={cn(
                            "h-2.5 w-2.5",
                            groupEmail.isStarred && "fill-current"
                          )}
                        />
                      </button>
                      {!groupEmail.isRead && (
                        <div className="w-1 h-1 rounded-full bg-blue-500" />
                      )}
                      <span className="text-[9px] text-muted-foreground">
                        {formatDate(groupEmail.receivedAt).formatted}
                      </span>
                      {groupEmail.aiAnalysis?.priority === "important" && (
                        <span
                          className="text-[8px] px-1 py-0.5 rounded bg-amber-500/15 text-amber-600 dark:text-amber-400 font-medium"
                          title="Important"
                        >
                          !
                        </span>
                      )}
                      {groupEmail.aiAnalysis?.priority === "low" && (
                        <span
                          className="text-[8px] px-1 py-0.5 rounded bg-gray-500/15 text-gray-500 font-medium"
                          title="Low Priority"
                        >
                          ↓
                        </span>
                      )}
                      {groupEmail.hasAttachments && (
                        <span title="Has attachments">
                          <IconPaperclip className="h-2.5 w-2.5 text-muted-foreground" />
                        </span>
                      )}
                    </div>

                    {/* Move to category dropdown */}
                    {onMoveEmail && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            onClick={(e) => e.stopPropagation()}
                            className={cn(
                              "p-1 rounded hover:bg-muted transition-colors",
                              "opacity-0 group-hover/email:opacity-100 focus:opacity-100"
                            )}
                            title="Move to category"
                          >
                            <IconGripVertical className="h-4 w-4 text-muted-foreground" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-40">
                          {currentCategory !== "do_nothing" && (
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                onMoveEmail(groupEmail, "do_nothing");
                              }}
                              className="gap-2"
                            >
                              <IconInbox className="h-4 w-4" />
                              Inbox
                            </DropdownMenuItem>
                          )}
                          {currentCategory !== "unsubscribe" && (
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                onMoveEmail(groupEmail, "unsubscribe");
                              }}
                              className="gap-2"
                            >
                              <IconCircleX className="h-4 w-4" />
                              Unsubscribe
                            </DropdownMenuItem>
                          )}
                          {currentCategory !== "archive" && (
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                onMoveEmail(groupEmail, "archive");
                              }}
                              className="gap-2"
                            >
                              <IconArchive className="h-4 w-4" />
                              Archive
                            </DropdownMenuItem>
                          )}
                          {currentCategory !== "delete" && (
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                onMoveEmail(groupEmail, "delete");
                              }}
                              className="gap-2 text-destructive"
                            >
                              <IconTrash className="h-4 w-4" />
                              Delete
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>

                  <h3
                    className={cn(
                      "text-xs truncate cursor-pointer",
                      !groupEmail.isRead && "font-semibold text-foreground",
                      groupEmail.isRead && "font-normal text-foreground/80"
                    )}
                    onClick={(e) => {
                      e.stopPropagation();
                      onClick?.(groupEmail, e);
                    }}
                  >
                    {groupEmail.subject}
                  </h3>

                  <div
                    className="flex items-start justify-between gap-2 cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      onClick?.(groupEmail, e);
                    }}
                  >
                    <p className="text-[11px] text-muted-foreground line-clamp-1 flex-1">
                      {groupEmail.snippet}
                    </p>
                    {/* Labels container - AI label, user labels, then info icon at end */}
                    <div className="flex items-center gap-1 shrink-0">
                      {renderLabels(groupEmail)}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function getPriorityConfig(priority?: string): PriorityConfig | null {
  switch (priority?.toLowerCase()) {
    case "important":
      return {
        className:
          "bg-[#D97706]/10 text-[#D97706] dark:text-[#FBD38D] px-2 py-0.5 rounded text-xs font-medium",
        tooltip: "Important Priority",
        label: "Important",
      };
    case "normal":
      return {
        className:
          "bg-[#059669]/10 text-[#059669] dark:text-[#6EE7B7] px-2 py-0.5 rounded text-xs font-medium",
        tooltip: "Normal Priority",
        label: "Normal",
      };
    case "low":
      return {
        className:
          "bg-[#6B7280]/10 text-[#6B7280] dark:text-[#D1D5DB] px-2 py-0.5 rounded text-xs font-medium",
        tooltip: "Low Priority",
        label: "Low",
      };
    default:
      return null;
  }
}
